import React from 'react';
import Footer from './Footer';
import Header from './Header';
import Seo from './Seo';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="neural-glass-homepage">
      <Seo
        title="Privacy Policy"
        description="How Terview AI collects, uses, and protects your personal data. Read our full privacy policy."
        path="/privacy-policy"
      />
      <div className="neural-background"></div>
      <div className="geometric-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
      </div>

      <Header setShowAuthModal={() => {}} />

      <main className="privacy-main">

        <div className="privacy-hero">
          <div className="privacy-hero-logo">
            <div className="privacy-hero-logo-icon">🎯</div>
            <div className="privacy-hero-logo-name">terview.ai</div>
            <div className="privacy-hero-logo-tagline">Instant Answers, Immediate Confidence</div>
          </div>
          <div className="privacy-hero-content">
            <h1 className="privacy-hero-title">Privacy Policy</h1>
            <p className="privacy-hero-desc">
              Read Terview AI's Privacy Policy to learn how we safeguard your data and ensure your privacy while using our AI-powered career solutions.
            </p>
            <div className="privacy-hero-meta">
              <div>
                <div className="privacy-hero-meta-label">WRITTEN BY</div>
                <div className="privacy-hero-meta-author">
                  <span className="privacy-hero-meta-name">Terview AI Team</span>
                </div>
              </div>
              <div>
                <div className="privacy-hero-meta-label">EFFECTIVE DATE</div>
                <div className="privacy-hero-meta-date">Mar 18, 2026</div>
              </div>
            </div>
          </div>
        </div>

        <div className="privacy-body">

          <Section title="Privacy Policy">
            <p><strong className="privacy-highlight">Effective Date:</strong> March 18, 2026</p>
            <p><strong className="privacy-highlight">Last Updated:</strong> March 18, 2026</p>
            <p>
              At Terview AI, your privacy is of utmost importance to us. We are committed to respecting your privacy and adhering to any applicable law and regulation regarding any personal information we may collect, including information collected through our website,{' '}
              <a href="https://terview.ai" className="privacy-link">https://terview.ai</a>, and other services we provide.
            </p>
          </Section>

          <Section title="Information We Collect">
            <p>We collect two types of information: voluntarily provided information and automatically collected information.</p>
            <ul>
              <BulletItem title="Voluntarily Provided Information">
                This includes information you give us directly, such as when you sign up for an account, subscribe to our services, upload your resume, or participate in our AI-powered interview sessions.
              </BulletItem>
              <BulletItem title="Automatically Collected Information">
                This includes information that your devices automatically send when you access our services. It encompasses data like your IP address, device type, browser type, and how you interact with our platform.
              </BulletItem>
            </ul>
          </Section>

          <Section title="Detailed Data Collection">
            <ul>
              <BulletItem title="Log Data">Our servers log information like your IP address, browser type, the pages you visit, and visit duration.</BulletItem>
              <BulletItem title="Device Data">We collect information about the device you use, such as the device type and operating system.</BulletItem>
              <BulletItem title="Personal Information">We may collect personal details like your name and email address when you engage with our services.</BulletItem>
              <BulletItem title="Resume & Career Data">When you use our resume builder, ATS checker, or interview assistant, we process the career information you provide to generate AI-powered responses. This data is used solely to power the features you request.</BulletItem>
              <BulletItem title="Interview Session Data">Audio recordings and transcripts generated during interview assistant sessions are processed in real-time and not stored beyond your active session unless you explicitly save them.</BulletItem>
              <BulletItem title="User-Generated Content">Any content provided by users will only be published with their explicit consent. Users can delete their content at any time through their account settings. Accounts inactive for over one year will have all associated data automatically deleted.</BulletItem>
            </ul>
          </Section>

          <Section title="Cookies and Tracking Technologies">
            <p>
              We use cookies and similar technologies to improve user experience, maintain login sessions, and analyze website performance. By using our services, you consent to our use of cookies. You may disable cookies in your browser settings, though this may affect some functionality of our platform.
            </p>
          </Section>

          <Section title="How We Use Your Information">
            <ul>
              <BulletItem title="Service Delivery">To provide, operate, and maintain our AI-powered interview, resume, and career tools.</BulletItem>
              <BulletItem title="Personalization">To personalize AI responses based on your resume and job description inputs.</BulletItem>
              <BulletItem title="Communication">To send you service-related emails, updates, and support responses.</BulletItem>
              <BulletItem title="Billing">To process subscription payments and manage your account credits.</BulletItem>
              <BulletItem title="Improvement">To analyze usage patterns and improve the platform.</BulletItem>
            </ul>
          </Section>

          <Section title="Security of Your Personal Information">
            <p>
              We strive to protect your personal information with commercially acceptable means against unauthorized access, loss, or theft. We use industry-standard encryption (HTTPS/TLS) for all data in transit. However, no digital platform can guarantee absolute security, and we encourage you to use strong, unique passwords for your account.
            </p>
          </Section>

          <Section title="Retention of Your Personal Information">
            <p>
              We retain your personal information as long as necessary to provide our services to you and comply with legal obligations. Interview session data that you choose not to save is deleted immediately after your session ends. When your account is deleted, all associated personal data is removed within 30 days.
            </p>
          </Section>

          <Section title="Children's Privacy">
            <p>
              Our services are not intended for children under 13 years of age, and we do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such information, please contact us immediately.
            </p>
          </Section>

          <Section title="Disclosure of Personal Information to Third Parties">
            <p>We may share your information with:</p>
            <ul>
              <BulletItem>Subsidiaries, affiliates, and third-party service providers to facilitate our services.</BulletItem>
              <BulletItem>Regulatory authorities, law enforcement, or in legal proceedings if required.</BulletItem>
            </ul>
            <p>We do not sell your personal information. Third-party services we use include:</p>
            <ul>
              <BulletItem title="Google Analytics">For website traffic and usage analytics.</BulletItem>
              <BulletItem title="Dodo Payments">For secure payment processing and subscription management.</BulletItem>
              <BulletItem title="Hostinger">For web hosting and server infrastructure.</BulletItem>
            </ul>
          </Section>

          <Section title="Your Rights">
            <p>You have the following rights over your personal information:</p>
            <ul>
              <BulletItem title="Correction">Request correction of any inaccurate information.</BulletItem>
              <BulletItem title="Deletion">Delete your entire account, including all personal data, by navigating to your profile settings. All data will be permanently removed within 30 days.</BulletItem>
              <BulletItem title="Data Portability">Request an export of your data in a portable format.</BulletItem>
              <BulletItem title="Opt-out">Unsubscribe from marketing communications at any time via the link in our emails.</BulletItem>
            </ul>
          </Section>

          <Section title="Changes to This Policy">
            <p>
              We may update this policy to reflect changes in our practices or for legal, operational, or regulatory reasons. Significant changes will be communicated through our website or directly to you via email. Continued use of our services after changes constitutes acceptance of the updated policy.
            </p>
          </Section>

          <Section title="Contact Us">
            <p>
              For questions about this privacy policy or to exercise your rights, please contact us at:{' '}
              <a href="mailto:support@terview.ai" className="privacy-link">support@terview.ai</a>
            </p>
            <p className="privacy-note">
              By using our services, you acknowledge you have read and understood this Privacy Policy.
            </p>
          </Section>

        </div>
      </main>

      <Footer />
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="privacy-section">
    <h2>{title}</h2>
    {children}
  </div>
);

const BulletItem = ({ title, children }) => (
  <li>
    {title && <strong>{title}: </strong>}
    {children}
  </li>
);

export default PrivacyPolicy;